(() => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const dataConstrained = connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const feedPageSize = 24;
  const poolSize = 8;
  const cacheVersion = 'v2';

  const shuffle = (items) => {
    const shuffled = [...items];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }
    return shuffled;
  };

  const getSessionPool = (key) => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(key) || 'null');
      return Array.isArray(stored) ? stored.filter((product) => product?.cover).slice(0, poolSize) : null;
    } catch (_) {
      return null;
    }
  };

  const setSessionPool = (key, products) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(products));
    } catch (_) {
      // Storage can be unavailable in privacy modes; rotation still works.
    }
  };

  const imageSources = (cover) => {
    if (!cover) return { src: '', srcset: '' };
    if (!cover.includes('images.igdb.com/igdb/image/upload/')) return { src: cover, srcset: '' };

    const small = cover
      .replace('/t_cover_big_2x/', '/t_cover_big/')
      .replace('/t_1080p_2x/', '/t_cover_big/');
    const large = small.replace('/t_cover_big/', '/t_cover_big_2x/');
    return { src: small, srcset: `${small} 264w, ${large} 528w` };
  };

  const fetchFeedPage = async (source, page) => {
    const url = new URL(source, window.location.origin);
    url.searchParams.set('page', String(page));
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Random system feed failed: ${response.status}`);
    const payload = await response.json();
    return Array.isArray(payload.products) ? payload.products : [];
  };

  const loadRandomPool = async (card) => {
    const source = card.dataset.rpgHomeRandomSource;
    const collectionKey = card.dataset.rpgHomeRandomKey || source;
    if (!source || !collectionKey) return [];

    const cacheKey = `rpg-home-starters:${cacheVersion}:${collectionKey}`;
    const cached = getSessionPool(cacheKey);
    if (cached?.length) return cached;

    const productCount = Math.max(0, Number(card.dataset.rpgHomeProductCount) || 0);
    const pageCount = Math.max(1, Math.ceil(productCount / feedPageSize));
    const firstPage = 1 + Math.floor(Math.random() * pageCount);
    let secondPage = firstPage;
    if (pageCount > 1) {
      secondPage = 1 + Math.floor(Math.random() * (pageCount - 1));
      if (secondPage >= firstPage) secondPage += 1;
    }

    const pages = await Promise.all([
      fetchFeedPage(source, firstPage),
      ...(secondPage !== firstPage ? [fetchFeedPage(source, secondPage)] : [])
    ]);
    const distinctGames = [...new Map(
      pages.flat().map((product) => [String(product.group || product.id), product])
    ).values()];
    const selected = shuffle(distinctGames).slice(0, poolSize);
    if (selected.length) setSessionPool(cacheKey, selected);
    return selected;
  };

  const prepareImage = async (image, product) => {
    const sources = imageSources(product?.cover);
    if (!sources.src) throw new Error('Starter cover is missing.');

    image.classList.remove('rpg-image-ready');
    image.removeAttribute('srcset');
    image.src = sources.src;
    if (sources.srcset) {
      image.srcset = sources.srcset;
      image.sizes = '(min-width: 1200px) 20vw, 31vw';
    }
    image.loading = 'eager';
    image.fetchPriority = 'low';
    await image.decode();
    if (!image.naturalWidth) throw new Error('Starter cover failed to decode.');
    image.classList.add('rpg-image-ready');
  };

  const initializeRotator = async (card, cardIndex) => {
    const slides = [...card.querySelectorAll('[data-rpg-home-system-slide]')];
    if (slides.length !== 2 || dataConstrained || reducedMotion) {
      card.dataset.rpgHomeRandomState = 'fallback';
      return;
    }

    let products;
    try {
      products = await loadRandomPool(card);
      if (!products.length) throw new Error('Random system feed returned no games.');
      card.dataset.rpgHomeRandomState = 'ready';
      card.dataset.rpgHomeRandomized = String(products.length);
      delete card.dataset.rpgHomeRandomError;
    } catch (error) {
      card.dataset.rpgHomeRandomState = 'error';
      card.dataset.rpgHomeRandomError = error instanceof Error ? error.message : String(error);
      console.warn('Using the server-rendered starter cover.', error);
      return;
    }

    let activeLayer = 0;
    let productIndex = 0;
    let timer = 0;
    let visible = true;
    let paused = false;
    let swapping = false;
    const delay = 3600 + (cardIndex * 420);

    const schedule = () => {
      window.clearTimeout(timer);
      if (!visible || paused || document.hidden) return;
      timer = window.setTimeout(showNext, delay);
    };

    const showNext = async () => {
      if (swapping || !visible || paused || document.hidden) return schedule();
      swapping = true;
      const incomingLayer = activeLayer === 0 ? 1 : 0;
      const incomingSlide = slides[incomingLayer];
      const incomingImage = incomingSlide.querySelector('img');
      const product = products[productIndex % products.length];

      try {
        await prepareImage(incomingImage, product);
        window.requestAnimationFrame(() => {
          slides[activeLayer].classList.remove('is-active');
          incomingSlide.classList.add('is-active');
          activeLayer = incomingLayer;
          productIndex = (productIndex + 1) % products.length;
          swapping = false;
          schedule();
        });
      } catch (_) {
        productIndex = (productIndex + 1) % products.length;
        swapping = false;
        schedule();
      }
    };

    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        schedule();
      }, { rootMargin: '0px', threshold: 0.01 });
      visibilityObserver.observe(card);
    }

    document.addEventListener('visibilitychange', schedule);
    card.addEventListener('mouseenter', () => { paused = true; window.clearTimeout(timer); });
    card.addEventListener('mouseleave', () => { paused = false; schedule(); });
    card.addEventListener('focusin', () => { paused = true; window.clearTimeout(timer); });
    card.addEventListener('focusout', () => { paused = false; schedule(); });
    schedule();
  };

  const initializeHome = (home) => {
    const form = home.querySelector('[data-rpg-home-search]');
    const localInput = form?.querySelector('input[type="search"]');

    const openRpgSearch = (term = '') => {
      const toggle = document.querySelector('.header .js-show-search:not(.hidden), .header .js-show-search-left');
      const headerInput = document.querySelector('.header .js-search-input');
      if (!toggle || !headerInput) return;

      if (document.querySelector('.header.search-is-collapsed, store-header.search-is-collapsed')) toggle.click();
      window.setTimeout(() => {
        headerInput.value = term;
        headerInput.dispatchEvent(new Event('input', { bubbles: true }));
        headerInput.focus();
      }, 340);
    };

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      openRpgSearch(localInput?.value.trim() || '');
    });

    const pageUrl = new URL(window.location.href);
    const redirectedTerm = pageUrl.searchParams.get('rpg-search');
    if (redirectedTerm !== null) {
      window.addEventListener('load', () => openRpgSearch(redirectedTerm), { once: true });
      pageUrl.searchParams.delete('rpg-search');
      window.history.replaceState({}, '', `${pageUrl.pathname}${pageUrl.search}${pageUrl.hash}`);
    }

    home.querySelectorAll('[data-rpg-home-system]').forEach(initializeRotator);
  };

  const initializeHomes = () => {
    document.querySelectorAll('[data-rpg-home]').forEach((home) => {
      if (home.dataset.rpgHomeInitialized === 'true') return;
      home.dataset.rpgHomeInitialized = 'true';
      initializeHome(home);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeHomes, { once: true });
  } else {
    initializeHomes();
  }

  document.addEventListener('shopify:section:load', initializeHomes);
})();
